'use client';

import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { $activitiesStore } from '@nx-frontend/features/notification-panel';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { useNavigateWithCheckUnsavedChanges } from '@nx-frontend/shared/hooks';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $orgChat, startFetchUnreadMessagesEvent } from '@nx-frontend/shared/store/org-chat';
import { openSearchEvent } from '@nx-frontend/shared/store/search';
import { $sidebar } from '@nx-frontend/shared/store/sidebar';
import { UiTooltipPosition } from '@nx-frontend/shared/types';

export function SidebarButtonsBlock({ onShowNotification }: { onShowNotification: () => void }) {
  const [currentWorkspace, currentOrganization, orgChat, sidebar, startFetchUnreadMessages, activitiesStore] = useUnit([
    $currentWorkspace,
    $currentOrganization,
    $orgChat,
    $sidebar,
    startFetchUnreadMessagesEvent,
    $activitiesStore,
  ]);
  const [openSearch] = useUnit([openSearchEvent]);

  const [chatIcon, setChatIcon] = useState(IconsMap.CHAT_1);

  const router = useRouter();
  const navigateParams = useMemo(() => {
    return {
      routerPush: (url: string) => router.push(url),
    };
  }, [router]);
  const navigateWithCheckUnsavedChanges = useNavigateWithCheckUnsavedChanges(navigateParams);

  const hasImportantActivities = useMemo(() => {
    return !!activitiesStore.workspaces[currentWorkspace.globalId]?.hasImportant || false;
  }, [activitiesStore, currentWorkspace]);

  useEffect(() => {
    const fetchUnreadMessages = async () => {
      startFetchUnreadMessages();
      const intervalId = setInterval(async () => {
        startFetchUnreadMessages();
      }, 60000);
      return () => {
        clearInterval(intervalId);
      };
    };
    fetchUnreadMessages();
  }, [currentOrganization, startFetchUnreadMessages]);

  useEffect(() => {
    if (orgChat.unreadMessagesCount > 0) {
      setChatIcon(IconsMap.CHAT_WITH_BADGE);
    } else setChatIcon(IconsMap.CHAT_1);
  }, [orgChat, chatIcon]);

  const handleSearchButtonClick = () => (e: React.MouseEvent) => {
    return openSearch();
  };

  const handleChatButtonClick = () => {
    navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/chat`);
  };

  return (
    <div className="flex w-full items-start gap-1">
      <TippyTooltip message="Updates" position={UiTooltipPosition.TOP} display="block">
        <button
          data-cy="sidebar-notification-button"
          className={classNames(
            'flex py-[6px] px-1 items-center justify-center rounded border border-solid border-neutral-200 w-full h-[26px] gap-2 hover:bg-neutral-100',
            {
              'h-[30px]': sidebar.width > 330,
            }
          )}
          onClick={onShowNotification}
        >
          <Icon
            icon={hasImportantActivities ? IconsMap.BELL_WITH_BADGE : IconsMap.BELL}
            className="w-4 h-4 text-neutral-500"
          />
          {sidebar.width > 330 ? <span className="text-neutral-800 text-[13px] font-medium">Updates</span> : null}
        </button>
      </TippyTooltip>
      <TippyTooltip message="Chat" position={UiTooltipPosition.TOP} display="block">
        <button
          data-cy="sidebar-chat-button"
          className={classNames(
            'flex py-[6px] px-1 items-center justify-center hover:bg-neutral-100 rounded border border-solid border-neutral-200 w-full h-[26px] gap-2',
            {
              'h-[30px]': sidebar.width > 330,
            }
          )}
          onClick={handleChatButtonClick}
        >
          <Icon icon={chatIcon} className="w-4 h-4 text-neutral-500" />
          {sidebar.width > 330 ? <span className="text-neutral-800 text-[13px] font-medium">Chat</span> : null}
        </button>
      </TippyTooltip>
      <TippyTooltip message="Search" position={UiTooltipPosition.TOP} display="block">
        <button
          data-cy="sidebar-search-button"
          className={classNames(
            'flex py-[6px] px-1 items-center justify-center hover:bg-neutral-100 rounded border border-solid border-neutral-200 w-full h-[26px] gap-2',
            {
              'h-[30px]': sidebar.width > 330,
            }
          )}
          onClick={handleSearchButtonClick()}
        >
          <Icon icon={IconsMap.SEARCH} className="w-4 h-4 text-neutral-500" />
          {sidebar.width > 330 ? <span className="text-neutral-800 text-[13px] font-medium">Search</span> : null}
        </button>
      </TippyTooltip>
    </div>
  );
}
